import { useEffect, useState } from "react";
import { Alert, Card, Segmented, Space, Typography } from "antd";
import { ancanaApi } from "../../../api/ancanaApi";

/** One plan as `ancana_api` fills it into a slide: the strings under
 * `listing.payment_plans.N`, finished. */
export interface PlanText {
  id: number;
  name: string;
  terms: string;
  total: string;
}

type Language = 'es' | 'en';

/**
 * Each plan as the brochure prints it, in a plain card.
 *
 * The text is fetched, not composed here: `unit-payment-plans/brochure-text/`
 * returns what the slide's tokens are filled with, so what this shows is the
 * slide's text and cannot drift from it. The card is only a box — the design
 * around the text (the "=" before the total, its underline) is the template's.
 *
 * `version` changes whenever the plans do, so an edit in the table refetches.
 */
function PlanPreview({ unitId, version }: { unitId: number | string, version: string }) {
  const [language, setLanguage] = useState<Language>('es');
  const [plans, setPlans] = useState<PlanText[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    ancanaApi.get<PlanText[]>('unit-payment-plans/brochure-text/', { params: { unit: unitId, language } })
      .then(({ data }) => { if (live) { setPlans(data); setFailed(false); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [unitId, language, version]);

  if (failed) {
    return <Alert type="warning" showIcon message="Could not load the brochure preview." />;
  }
  if (!plans?.length) {
    return null;
  }
  return (
    <div className="mt-lg">
      <Space align="center" style={{ marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>As the brochure prints them</Typography.Title>
        <Segmented size="small" value={language} onChange={value => setLanguage(value as Language)}
                   options={[{ label: 'ES', value: 'es' }, { label: 'EN', value: 'en' }]} />
      </Space>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {plans.map(plan => (
          <Card key={plan.id} size="small" title={plan.name} style={{ width: 260, textAlign: 'center' }}>
            {/* `terms` arrives as lines; the slide breaks them the same way. */}
            <div style={{ whiteSpace: 'pre-line' }}>{plan.terms || '—'}</div>
            {plan.total && (
              <div className="mt-sm">
                <Typography.Text type="secondary">Total </Typography.Text>
                <Typography.Text strong>{plan.total}</Typography.Text>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

export default PlanPreview;
